import React, { useState, useEffect, useRef } from 'react';
import { User, Users, Shield, Upload, Save, Trash2, Camera, FileBadge, Building } from 'lucide-react';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile State
  const [profile, setProfile] = useState({ id: 1, nome: '', email: '', foto_url: '' });
  const [newPhoto, setNewPhoto] = useState(null);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '' });
  const fileInputRef = useRef(null);

  // Users State
  const [usersList, setUsersList] = useState([]);
  const [newUser, setNewUser] = useState({ nome: '', email: '' });

  // Certificate State
  const [certName, setCertName] = useState('');
  const [certFile, setCertFile] = useState(null);

  // Fiscal State
  const [fiscalData, setFiscalData] = useState({
    cnpj: '', razao_social: '', nome_fantasia: '', ie: '', im: '', cnae: '', crt: 1,
    logradouro: '', numero: '', complemento: '', bairro: '', cMun: '', xMun: '', uf: 'SP', cep: '',
    ambiente_nfe: 2, serie_nfe: 1, proxima_nfe: 1
  });

  useEffect(() => {
    fetchProfile();
    fetchUsers();
    fetchConfig();
  }, []);

  const fetchProfile = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/usuarios.php?id=${userId}`);
      const data = await response.json();
      if (data && data.length > 0) {
        setProfile(data[0]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/usuarios.php`);
      const data = await response.json();
      setUsersList(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/configuracoes.php`);
      const data = await response.json();
      if (data) {
        if (data.certificado_nome) setCertName(data.certificado_nome);
        setFiscalData({
          cnpj: data.cnpj || '', razao_social: data.razao_social || '', nome_fantasia: data.nome_fantasia || '',
          ie: data.ie || '', im: data.im || '', cnae: data.cnae || '', crt: data.crt || 1,
          logradouro: data.logradouro || '', numero: data.numero || '', complemento: data.complemento || '',
          bairro: data.bairro || '', cMun: data.cMun || '', xMun: data.xMun || '', uf: data.uf || 'SP', cep: data.cep || '',
          ambiente_nfe: data.ambiente_nfe || 2, serie_nfe: data.serie_nfe || 1, proxima_nfe: data.proxima_nfe || 1
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  // -- Profile Handlers --
  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    
    const formData = new FormData();
    formData.append('id', userId);
    formData.append('nome', profile.nome);
    formData.append('email', profile.email);
    if (newPhoto) {
      formData.append('foto', newPhoto);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php?action=update`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Aviso','Perfil atualizado com sucesso!');
        fetchProfile();
        // Disparar evento para atualizar o Topbar
        window.dispatchEvent(new Event('profileUpdated'));
      } else {
        showAlert('Aviso','Erro ao atualizar: ' + data.error);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    if (!passwordData.currentPassword || !passwordData.newPassword) {
      showAlert('Aviso', 'Preencha todos os campos de senha.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php?action=change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: userId,
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Sucesso', 'Sua senha foi redefinida!');
        setPasswordData({ currentPassword: '', newPassword: '' });
      } else {
        showAlert('Erro', data.error);
      }
    } catch (e) {
      console.error(e);
      showAlert('Erro', 'Falha na comunicação com o servidor.');
    }
  };

  // -- Users Handlers --
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.nome || !newUser.email) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      const data = await res.json();
      if (data.success) {
        setNewUser({ nome: '', email: '' });
        fetchUsers();
      } else {
        showAlert('Aviso','Erro ao criar usuário');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteUser = async (id) => {
    if (id === 1) {
      showAlert('Aviso','Não é possível apagar o administrador principal!');
      return;
    }
    const confirmed = await showConfirm('Confirmação', 'Apagar este usuário?');
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_BASE_URL}/usuarios.php?id=${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      } else {
        showAlert('Aviso',data.error);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // -- Cert Handlers --
  const handleCertUpload = async (e) => {
    e.preventDefault();
    if (!certFile) return;

    const formData = new FormData();
    formData.append('certificado', certFile);

    try {
      const res = await fetch(`${API_BASE_URL}/configuracoes.php?action=upload_cert`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Aviso','Certificado enviado!');
        setCertName(data.certificado_nome);
        setCertFile(null);
      } else {
        showAlert('Aviso','Erro: ' + data.error);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // -- Fiscal Handlers --
  const handleFiscalChange = (e) => {
    setFiscalData({ ...fiscalData, [e.target.name]: e.target.value });
  };

  const handleSaveFiscal = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/configuracoes.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fiscalData)
      });
      const data = await res.json();
      if (data.success) {
        showAlert('Aviso', 'Dados Fiscais salvos com sucesso!');
        fetchConfig();
      } else {
        showAlert('Aviso', 'Erro ao salvar: ' + data.error);
      }
    } catch (e) {
      console.error(e);
      showAlert('Aviso', 'Erro de conexão');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Configurações</h1>
          <p className="page-subtitle">Gerencie seu perfil, usuários do sistema e segurança</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        
        {/* SIDEBAR TABS */}
        <div className="card" style={{ width: '280px', flexShrink: 0, padding: '1rem', alignSelf: 'flex-start' }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'profile' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'profile' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'profile' ? '600' : '400' }}
                onClick={() => setActiveTab('profile')}
              >
                <User size={18} style={{ marginRight: '0.75rem' }} /> Meu Perfil
              </button>
            </li>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'users' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'users' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'users' ? '600' : '400' }}
                onClick={() => setActiveTab('users')}
              >
                <Users size={18} style={{ marginRight: '0.75rem' }} /> Usuários do Sistema
              </button>
            </li>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'cert' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'cert' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'cert' ? '600' : '400' }}
                onClick={() => setActiveTab('cert')}
              >
                <Shield size={18} style={{ marginRight: '0.75rem' }} /> Certificado Digital
              </button>
            </li>
            <li>
              <button 
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0.75rem 1rem', background: activeTab === 'fiscal' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'fiscal' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.2s', fontWeight: activeTab === 'fiscal' ? '600' : '400' }}
                onClick={() => setActiveTab('fiscal')}
              >
                <Building size={18} style={{ marginRight: '0.75rem' }} /> Dados Fiscais (NF-e)
              </button>
            </li>
          </ul>
        </div>

        {/* CONTENT AREA */}
        <div className="card" style={{ flex: '1', minWidth: '400px', padding: '2rem' }}>
          
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Meu Perfil</h2>
              <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '500px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                  <div 
                    style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                    onClick={() => fileInputRef.current.click()}
                  >
                    {newPhoto ? (
                      <img src={URL.createObjectURL(newPhoto)} alt="Novo perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : profile.foto_url ? (
                      <img src={profile.foto_url.startsWith('http') ? profile.foto_url : `${API_BASE_URL.replace('/src/php', '')}/${profile.foto_url}`} alt="Perfil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <User size={32} color="var(--text-muted)" />
                    )}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', padding: '2px' }}>
                      <Camera size={14} color="#fff" />
                    </div>
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Foto de Perfil</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Clique na imagem para alterar</p>
                    <input 
                      type="file" 
                      accept="image/*" 
                      ref={fileInputRef} 
                      style={{ display: 'none' }} 
                      onChange={(e) => setNewPhoto(e.target.files[0])}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Nome Completo</label>
                  <input type="text" name="nome" value={profile.nome} onChange={handleProfileChange} className="standard-input" required />
                </div>
                <div className="form-group">
                  <label>E-mail (Login)</label>
                  <input type="email" name="email" value={profile.email} onChange={handleProfileChange} className="standard-input" required />
                </div>
                
                <button type="submit" className="btn-success" style={{ alignSelf: 'flex-start' }}>
                  <Save size={18} style={{ marginRight: '0.5rem' }} /> Salvar Alterações
                </button>
              </form>

              <hr style={{ margin: '2rem 0', borderColor: 'var(--border-color)', borderStyle: 'solid', borderWidth: '1px 0 0 0' }} />

              <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Trocar Senha</h3>
              <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '500px' }}>
                <div className="form-group">
                  <label>Senha Atual</label>
                  <input 
                    type="password" 
                    value={passwordData.currentPassword} 
                    onChange={e => setPasswordData({...passwordData, currentPassword: e.target.value})} 
                    className="standard-input" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Nova Senha</label>
                  <input 
                    type="password" 
                    value={passwordData.newPassword} 
                    onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})} 
                    className="standard-input" 
                    required 
                  />
                </div>
                
                <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>
                  <Shield size={18} style={{ marginRight: '0.5rem' }} /> Redefinir Senha
                </button>
              </form>
            </div>
          )}

          {/* USERS TAB */}
          {activeTab === 'users' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Usuários do Sistema</h2>
              
              <form onSubmit={handleCreateUser} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '2rem', padding: '1rem', backgroundColor: 'var(--bg-color)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <div className="form-group flex-1">
                  <label>Nome</label>
                  <input type="text" value={newUser.nome} onChange={e => setNewUser({...newUser, nome: e.target.value})} className="standard-input" required placeholder="Nome do novo usuário" />
                </div>
                <div className="form-group flex-1">
                  <label>E-mail</label>
                  <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="standard-input" required placeholder="email@exemplo.com" />
                </div>
                <button type="submit" className="btn-primary" style={{ height: '38px' }}>Adicionar</button>
              </form>

              <div className="table-responsive" style={{ border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Usuário</th>
                      <th>Nível</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map(u => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>
                          <div className="font-medium">{u.nome}</div>
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>{u.email}</div>
                        </td>
                        <td><span className="badge badge-success">Admin</span></td>
                        <td>
                          {u.id !== '1' && u.id !== 1 && (
                            <button className="btn-outline" style={{ borderColor: 'transparent', color: 'var(--danger-color)', display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={() => handleDeleteUser(u.id)} title="Excluir">
                              <Trash2 size={16} /> Remover Acesso
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CERTIFICATE TAB */}
          {activeTab === 'cert' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Certificado Digital</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Faça o upload do seu certificado e-CNPJ ou e-CPF (formato .pfx) para emissão de notas fiscais.</p>

              {certName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '8px', marginBottom: '1.5rem', backgroundColor: 'var(--bg-color)' }}>
                  <FileBadge size={32} color="var(--success-color)" />
                  <div>
                    <h4 style={{ margin: 0, color: 'var(--text-main)' }}>
                      Certificado Ativo
                    </h4>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{certName}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleCertUpload} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px' }}>
                <div className="form-group">
                  <label>Selecionar Arquivo (.pfx)</label>
                  <input 
                    type="file" 
                    accept=".pfx"
                    className="standard-input" 
                    onChange={e => setCertFile(e.target.files[0])}
                    style={{ padding: '0.4rem' }}
                    required
                  />
                </div>
                <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start' }}>
                  <Upload size={18} style={{ marginRight: '0.5rem' }} /> Enviar Certificado
                </button>
              </form>
            </div>
          )}

          {/* FISCAL TAB */}
          {activeTab === 'fiscal' && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-main)' }}>Dados Fiscais (NF-e)</h2>
              <form onSubmit={handleSaveFiscal} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                
                {/* Identificação */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>1. Identificação da Empresa</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>CNPJ <span className="text-danger">*</span></label>
                      <input type="text" name="cnpj" value={fiscalData.cnpj} onChange={handleFiscalChange} className="standard-input" required placeholder="Apenas números" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Razão Social <span className="text-danger">*</span></label>
                      <input type="text" name="razao_social" value={fiscalData.razao_social} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Nome Fantasia</label>
                      <input type="text" name="nome_fantasia" value={fiscalData.nome_fantasia} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group">
                      <label>Inscrição Estadual (IE) <span className="text-danger">*</span></label>
                      <input type="text" name="ie" value={fiscalData.ie} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Inscrição Municipal (IM)</label>
                      <input type="text" name="im" value={fiscalData.im} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group">
                      <label>CNAE Fiscal</label>
                      <input type="text" name="cnae" value={fiscalData.cnae} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Regime Tributário (CRT) <span className="text-danger">*</span></label>
                      <select name="crt" value={fiscalData.crt} onChange={handleFiscalChange} className="standard-input" required>
                        <option value={1}>1 - Simples Nacional</option>
                        <option value={2}>2 - Simples Nacional (Excesso de Sublimite)</option>
                        <option value={3}>3 - Regime Normal (Lucro Presumido/Real)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>2. Endereço</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>CEP <span className="text-danger">*</span></label>
                      <input type="text" name="cep" value={fiscalData.cep} onChange={handleFiscalChange} className="standard-input" required placeholder="00000000" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Logradouro (Rua, Av.) <span className="text-danger">*</span></label>
                      <input type="text" name="logradouro" value={fiscalData.logradouro} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Número <span className="text-danger">*</span></label>
                      <input type="text" name="numero" value={fiscalData.numero} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Complemento</label>
                      <input type="text" name="complemento" value={fiscalData.complemento} onChange={handleFiscalChange} className="standard-input" />
                    </div>
                    <div className="form-group">
                      <label>Bairro <span className="text-danger">*</span></label>
                      <input type="text" name="bairro" value={fiscalData.bairro} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>Código Município (IBGE) <span className="text-danger">*</span></label>
                      <input type="text" name="cMun" value={fiscalData.cMun} onChange={handleFiscalChange} className="standard-input" required placeholder="Ex: 3550308" />
                    </div>
                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                      <label>Nome do Município <span className="text-danger">*</span></label>
                      <input type="text" name="xMun" value={fiscalData.xMun} onChange={handleFiscalChange} className="standard-input" required />
                    </div>
                    <div className="form-group">
                      <label>UF <span className="text-danger">*</span></label>
                      <input type="text" name="uf" value={fiscalData.uf} onChange={handleFiscalChange} className="standard-input" required maxLength="2" />
                    </div>
                  </div>
                </div>

                {/* Emissão NF-e */}
                <div>
                  <h3 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem', marginBottom: '1rem', color: 'var(--text-main)' }}>3. Parâmetros de Emissão</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Ambiente Sefaz <span className="text-danger">*</span></label>
                      <select name="ambiente_nfe" value={fiscalData.ambiente_nfe} onChange={handleFiscalChange} className="standard-input" required>
                        <option value={2}>2 - Homologação (Testes)</option>
                        <option value={1}>1 - Produção (Validade Jurídica)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Série da NF-e <span className="text-danger">*</span></label>
                      <input type="number" name="serie_nfe" value={fiscalData.serie_nfe} onChange={handleFiscalChange} className="standard-input" required min="1" />
                    </div>
                    <div className="form-group">
                      <label>Próxima NF-e <span className="text-danger">*</span></label>
                      <input type="number" name="proxima_nfe" value={fiscalData.proxima_nfe} onChange={handleFiscalChange} className="standard-input" required min="1" />
                    </div>
                  </div>
                </div>

                <button type="submit" className="btn-success" style={{ alignSelf: 'flex-start', marginTop: '1rem', padding: '0.75rem 2rem' }}>
                  <Save size={18} style={{ marginRight: '0.5rem' }} /> Salvar Dados Fiscais
                </button>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
